/**
 * Human Resources · messages.
 *
 * Every SMS and email the platform owes people about their requests and
 * changes: received, approved, rejected, issued, status or check-in changed.
 * Whether each channel is set up, what went, what did not and why, and a way
 * to send again. Nothing here pretends a message went when it did not.
 */
import { useState } from "react";
import { useHrNotifications, useHrSendNow } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, Field, Select, Skeleton } from "../../design/components";
import { NOTIFY_STATUS, type NotifyStatus } from "../../domain/hrClock";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { useHrAccess } from "./access";
import { PreviewBar } from "./kit";
import { Channels, MessageList } from "./Messages";
import { HrError, s, SampleBanner, SUITE } from "./shared";

export function HrMessagesPage() {
  const session = useSession();
  const { can } = useHrAccess();
  const [status, setStatus] = useState("");
  const [channel, setChannel] = useState("");
  const q = useHrNotifications(status ? { status } : undefined);
  const send = useHrSendNow();
  const items = (q.data?.notifications ?? []).filter((n) => !channel || n.channel === channel);
  const counts = (q.data?.notifications ?? []).reduce<Record<string, number>>(
    (a, n) => ({ ...a, [n.status]: (a[n.status] ?? 0) + 1 }),
    {},
  );

  useScreenContext(
    q.data
      ? {
          route: "/hr-notifications",
          title: "Messages",
          summary: [
            t("{n} messages: {s} sent, {w} not sent.", {
              n: q.data.notifications.length,
              s: counts.sent ?? 0,
              w: q.data.notifications.length - (counts.sent ?? 0),
            }),
          ],
          offers: [{ label: t("Why were messages not sent?"), ask: "why were the HR SMS and emails not sent?" }],
          worksInFull: true,
        }
      : null,
  );

  return (
    <Page
      eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })}
      title="Messages"
      actions={
        can.admin ? (
          <Button variant="primary" onClick={() => send.mutate()} disabled={send.isPending} data-testid="hr-send-now">
            📤 {t("Send what is waiting")}
          </Button>
        ) : undefined
      }
    >
      <PreviewBar />
      <SampleBanner />
      <p className={s.note}>
        {t(
          "Each request and answer is queued for the person by SMS and email, Arabic first. Confidential records are never described.",
        )}
      </p>
      {q.isLoading && <Skeleton rows={6} />}
      {q.error && <HrError error={q.error} retry={() => void q.refetch()} />}
      {q.data && (
        <>
          <Card title={t("Sending")}>
            <Channels c={q.data.channels} />
            {send.data && (
              <p className={s.note}>
                {t("Sent {a}; not sent {b} (no provider); failed {c}.", {
                  a: send.data.sent,
                  b: send.data.not_configured,
                  c: send.data.failed,
                })}
              </p>
            )}
          </Card>
          <Card title={t("The outbox")} sub={t("Click a message to read it.")}>
            <div className={s.filters}>
              <Field label="Status">
                <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="">{t("Any")}</option>
                  {(Object.keys(NOTIFY_STATUS) as NotifyStatus[]).map((k) => (
                    <option key={k} value={k}>
                      {t(NOTIFY_STATUS[k].label)} {counts[k] ? `(${counts[k]})` : ""}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Channel">
                <Select value={channel} onChange={(e) => setChannel(e.target.value)}>
                  <option value="">{t("SMS and email")}</option>
                  <option value="sms">SMS</option>
                  <option value="email">{t("Email")}</option>
                </Select>
              </Field>
            </div>
            <MessageList items={items.slice(0, 300)} />
          </Card>
        </>
      )}
    </Page>
  );
}
